/**
 * The voice call as a pure state machine: `transition(state, event)` returns the next state and the effects
 * the engine should run (open or pause the mic, play or stop speech, send a turn, haptics, earcons...).
 * No Vue, audio, network or timers in here, so every transition is unit-tested.
 *
 * Half-duplex (v1): the mic is paused while Lighthouse speaks; tapping the orb interrupts. The mic stays
 * open while a run works ("you can keep talking"); what the creator says then goes to the run right away when
 * the server can steer it (`canSteer`, from CONFIG: the run takes it at its next step), else it's queued and
 * sent when the run finishes. "stop" / "cancel" while it works cancels the run; "hang up" ends the call; "say
 * that again" replays. Approvals are tap-only.
 */
import { matchIntent } from './intents'
import type { CallEffect, CallEvent, CallPhase, CallState } from './types'

export function initialCallState(): CallState {
    return {
        phase: 'idle',
        flags: { muted: false, speaker: true, minimized: false, reconnecting: false },
        error: null,
        endReason: null,
        connected: false,
        runActive: false,
        pendingApproval: false,
        speechWaiting: false,
        queued: '',
        heldFrom: null,
        turns: 0,
        sttFailures: 0,
        canSteer: false,
    }
}

/** A call is on (it shows a screen or pill and holds the mic). */
export function isLive(phase: CallPhase): boolean {
    return phase !== 'idle' && phase !== 'ended' && phase !== 'error'
}

/** Phases where the mic is open (unless muted). */
const HEARING: CallPhase[] = ['listening', 'userSpeaking', 'thinking', 'needsApproval']
const HOLDABLE: CallPhase[] = ['listening', 'userSpeaking', 'sending', 'thinking', 'speaking', 'needsApproval']

export const LINES = {
    usage: "You've reached your plan's limit, so I can't take that one right now.",
    network: "Sorry, I couldn't send that. Mind saying it again?",
    stillThere: 'Still there?',
}

function join(a: string, b: string): string {
    return [a.trim(), b.trim()].filter(Boolean).join(' ')
}

export function transition(prev: CallState, e: CallEvent): { state: CallState; effects: CallEffect[] } {
    const s: CallState = { ...prev, flags: { ...prev.flags } }
    const fx: CallEffect[] = []
    const out = () => ({ state: s, effects: fx })
    const micOn = () => { if (!s.flags.muted) fx.push({ type: 'stt', op: 'resume' }) }

    /** Nothing is playing: speak what's waiting, send what's queued, or go back to listening / thinking. */
    function rest(tick = false, mic = true) {
        if (s.speechWaiting) {
            s.speechWaiting = false
            s.phase = 'speaking'
            fx.push({ type: 'stt', op: 'pause' }, { type: 'tts', op: 'release' })
            return
        }
        if (!s.runActive && s.queued) {
            const text = s.queued
            s.queued = ''
            s.phase = 'sending'
            fx.push({ type: 'send', text })
            return
        }
        s.phase = s.pendingApproval ? 'needsApproval' : s.runActive ? 'thinking' : 'listening'
        if (tick && s.phase === 'listening') fx.push({ type: 'earcon', kind: 'tick' })
        if (mic) micOn()
    }

    function end(reason: NonNullable<CallState['endReason']>) {
        if (s.phase === 'idle' || s.phase === 'ended') return out()
        if (s.phase === 'error' && !s.connected) {
            Object.assign(s, initialCallState())
            fx.push({ type: 'cleanup' })
            return out()
        }
        s.phase = 'ended'
        s.endReason = reason
        s.speechWaiting = false
        fx.push(
            { type: 'tts', op: 'stop' },
            { type: 'stt', op: 'stop' },
            { type: 'holdTimer', op: 'clear' },
            { type: 'wakeLock', on: false },
            { type: 'haptic', kind: 'heavy' },
            { type: 'earcon', kind: 'end' },
        )
        if (s.connected) fx.push({ type: 'postCall', status: 'ended' })
        return out()
    }

    function fail(kind: NonNullable<CallState['error']>) {
        s.phase = 'error'
        s.error = kind
        s.speechWaiting = false
        fx.push(
            { type: 'stt', op: 'stop' },
            { type: 'tts', op: 'stop' },
            { type: 'holdTimer', op: 'clear' },
            { type: 'wakeLock', on: false },
            { type: 'haptic', kind: 'error' },
        )
        return out()
    }

    switch (e.type) {
        case 'CONFIG':
            s.canSteer = e.steer
            return out()

        case 'START':
            if (isLive(s.phase)) return out()
            Object.assign(s, { ...initialCallState(), canSteer: s.canSteer })
            s.phase = 'connecting'
            fx.push({ type: 'haptic', kind: 'medium' }, { type: 'wakeLock', on: true }, { type: 'stt', op: 'start' })
            return out()

        case 'READY':
            s.sttFailures = 0
            if (s.phase !== 'connecting') return out()
            s.phase = 'listening'
            fx.push({ type: 'earcon', kind: 'connect' }, { type: 'haptic', kind: 'medium' })
            if (!s.connected) fx.push({ type: 'timer', op: 'start' }, { type: 'postCall', status: 'started' })
            s.connected = true
            if (s.flags.muted) fx.push({ type: 'stt', op: 'pause' })
            return out()

        case 'FAIL':
            if (s.phase === 'ended') return out()
            return fail(e.kind)

        case 'RETRY':
            if (s.phase !== 'error') return out()
            s.phase = 'connecting'
            s.error = null
            s.sttFailures = 0
            fx.push({ type: 'wakeLock', on: true }, { type: 'stt', op: 'start' })
            return out()

        case 'SPEECH_START':
            if (!s.flags.muted && ['listening', 'thinking', 'needsApproval'].includes(s.phase)) s.phase = 'userSpeaking'
            return out()

        case 'PARTIAL':
            if (e.text.trim() && !s.flags.muted && ['listening', 'thinking', 'needsApproval'].includes(s.phase)) s.phase = 'userSpeaking'
            return out()

        case 'END_OF_TURN':
            if (s.phase !== 'userSpeaking' && s.phase !== 'listening') return out()
            // Muted: a turn end is stale (a mic that reopened late); what was heard is never sent.
            if (s.flags.muted) return out()
            s.phase = 'sending'
            fx.push({ type: 'stt', op: 'finish' }, { type: 'haptic', kind: 'light' })
            return out()

        case 'TAP_ORB':
            if (s.phase === 'speaking') {
                s.speechWaiting = false
                fx.push({ type: 'tts', op: 'stop' }, { type: 'silenceRun' }, { type: 'haptic', kind: 'light' })
                rest()
                return out()
            }
            if (s.phase === 'userSpeaking') {
                s.phase = 'sending'
                fx.push({ type: 'stt', op: 'finish' }, { type: 'haptic', kind: 'light' })
            }
            return out()

        case 'TRANSCRIPT': {
            if (s.phase !== 'sending') return out()
            const text = e.text.trim()
            if (!text) {
                rest()
                return out()
            }
            const intent = matchIntent(text)
            if (intent === 'hangup') return end('intent')
            if (intent === 'stop') {
                if (s.runActive) fx.push({ type: 'cancelRun' })
                s.queued = ''
                rest()
                return out()
            }
            if (intent === 'repeat') {
                fx.push({ type: 'replay' })
                rest()
                return out()
            }
            if (s.runActive && s.canSteer) {
                // The run takes it mid-task (a correction, more detail, a question about how it's going).
                fx.push({ type: 'send', text: join(s.queued, text), steer: true })
                s.queued = ''
                return out()
            }
            if (s.runActive) {
                s.queued = join(s.queued, text)
                rest()
                return out()
            }
            // Words queued while an earlier run worked go out with this turn, in the order they were said.
            const merged = join(s.queued, text)
            s.queued = ''
            fx.push({ type: 'send', text: merged })
            return out()
        }

        case 'SENT':
            s.turns += 1
            // The run's events can land before the send returns (it may already be over).
            s.runActive = e.active ?? true
            if (s.phase === 'sending') rest()
            return out()

        case 'SEND_FAILED':
            if (e.kind === 'busy') {
                // A server that can't steer said busy: wait for the run like before.
                s.queued = join(e.text, s.queued)
                s.runActive = true
                if (s.phase === 'sending') rest()
                return out()
            }
            // Say why; the mic reopens once that's been said.
            if (s.phase === 'sending') rest(false, false)
            fx.push({ type: 'haptic', kind: 'error' }, { type: 'say', text: LINES[e.kind] })
            return out()

        case 'SPEAKABLE':
            if (['listening', 'thinking', 'needsApproval'].includes(s.phase)) {
                s.phase = 'speaking'
                fx.push({ type: 'stt', op: 'pause' }, { type: 'haptic', kind: 'selection' })
            } else if (s.phase === 'userSpeaking' || s.phase === 'sending') {
                if (!s.speechWaiting) fx.push({ type: 'tts', op: 'hold' })
                s.speechWaiting = true
            }
            return out()

        case 'TTS_DRAINED':
            if (s.phase === 'speaking') rest(true)
            return out()

        case 'RUN_ACTIVE':
            s.runActive = true
            if (s.phase === 'listening') s.phase = 'thinking'
            return out()

        case 'RUN_IDLE':
            s.runActive = false
            if (s.phase === 'thinking') rest(true)
            return out()

        case 'APPROVAL_NEEDED':
            if (s.pendingApproval) return out()
            s.pendingApproval = true
            fx.push({ type: 'haptic', kind: 'warning' })
            if (s.phase === 'listening' || s.phase === 'thinking') s.phase = 'needsApproval'
            return out()

        case 'APPROVAL_RESOLVED':
            if (!s.pendingApproval) return out()
            s.pendingApproval = false
            if (s.phase === 'needsApproval') rest()
            return out()

        case 'TOGGLE_MUTE':
            if (!isLive(s.phase)) return out()
            s.flags.muted = !s.flags.muted
            fx.push({ type: 'haptic', kind: 'light' })
            if (s.flags.muted) {
                if (HEARING.includes(s.phase)) fx.push({ type: 'stt', op: 'pause' })
                if (s.phase === 'userSpeaking') s.phase = s.pendingApproval ? 'needsApproval' : s.runActive ? 'thinking' : 'listening'
            } else if (HEARING.includes(s.phase)) {
                fx.push({ type: 'stt', op: 'resume' })
            }
            return out()

        case 'SPEAKER':
            if (s.flags.speaker === e.on) return out()
            s.flags.speaker = e.on
            fx.push({ type: 'setSpeaker', on: e.on }, { type: 'haptic', kind: 'selection' })
            return out()

        case 'HOLD':
            if (!HOLDABLE.includes(s.phase)) return out()
            s.heldFrom = s.phase
            s.phase = 'onHold'
            s.speechWaiting = false
            fx.push({ type: 'tts', op: 'stop' }, { type: 'stt', op: 'stop' }, { type: 'holdTimer', op: 'start' })
            return out()

        case 'RESUME':
            if (s.phase !== 'onHold') return out()
            s.heldFrom = null
            s.phase = s.pendingApproval ? 'needsApproval' : s.runActive ? 'thinking' : 'listening'
            fx.push({ type: 'holdTimer', op: 'clear' }, { type: 'stt', op: 'start' }, { type: 'earcon', kind: 'connect' })
            if (s.flags.muted) fx.push({ type: 'stt', op: 'pause' })
            return out()

        case 'HOLD_TIMEOUT':
            if (s.phase !== 'onHold') return out()
            return end('interrupted')

        case 'STT_ERROR':
            if (!HOLDABLE.includes(s.phase) && s.phase !== 'connecting') return out()
            if (e.kind === 'permission') return fail('permission')
            s.sttFailures += 1
            if (s.sttFailures >= 3) return fail(e.kind === 'network' ? 'network' : 'unavailable')
            if (s.phase === 'userSpeaking') rest()
            fx.push({ type: 'stt', op: 'start' })
            return out()

        case 'IDLE_PROMPT':
            if (s.phase === 'listening' && !s.runActive) fx.push({ type: 'say', text: LINES.stillThere })
            return out()

        case 'IDLE_TIMEOUT':
            if (s.phase !== 'listening' || s.runActive) return out()
            return end('idle')

        case 'MINIMIZE':
            s.flags.minimized = true
            return out()

        case 'EXPAND':
            s.flags.minimized = false
            return out()

        case 'RECONNECTING':
            s.flags.reconnecting = e.on
            return out()

        case 'END':
            return end(e.reason)

        case 'DISMISS':
            if (s.phase !== 'ended' && s.phase !== 'error') return out()
            Object.assign(s, initialCallState())
            fx.push({ type: 'cleanup' })
            return out()

        // Realtime calls: full duplex. The voice model detects turns and is interrupted by the creator itself,
        // so these only move the phase the screen shows; the mic stays open (muting is the only pause).
        case 'RT_USER':
            if (!isLive(s.phase) || s.phase === 'onHold' || s.phase === 'connecting' || s.flags.muted) return out()
            if (e.speaking) s.phase = 'userSpeaking'
            else if (s.phase === 'userSpeaking') s.phase = s.pendingApproval ? 'needsApproval' : s.runActive ? 'thinking' : 'listening'
            return out()

        case 'RT_AGENT':
            if (!isLive(s.phase) || s.phase === 'onHold' || s.phase === 'connecting') return out()
            if (e.speaking) {
                if (s.phase !== 'speaking') fx.push({ type: 'haptic', kind: 'selection' })
                s.phase = 'speaking'
            } else if (s.phase === 'speaking') {
                s.phase = s.pendingApproval ? 'needsApproval' : s.runActive ? 'thinking' : 'listening'
                if (s.phase === 'listening') fx.push({ type: 'earcon', kind: 'tick' })
            }
            return out()
    }
    return out()
}
